/**
 * Static routes.
 *
 * Explains routing behavior, guards, and response semantics.
 */
import { resolveCustomerRouteMode } from '../../customer-auth/routes'
import { localeToLanguage, type ShopLocale } from '../../i18n/shopLocale'
import type { ShopSeoData } from '../seo'
import type { Route } from '../types'

/**
 * Build static route seo.
 *
 * Explains routing behavior, guards, and response semantics.
 */
export const buildStaticRouteSeo = (
  route: Route,
  siteName: string,
  locale: ShopLocale,
): ShopSeoData | null => {
  const language = localeToLanguage(locale)

  if (route.mode === 'page' && route.slug.trim().toLowerCase() === 'cart') {
    return {
      title: `${language === 'en' ? 'Cart' : 'Carrello'} | ${siteName}`,
      description:
        language === 'en'
          ? 'Manage products in your cart before checkout.'
          : 'Gestisci i prodotti aggiunti al carrello prima del checkout.',
      canonicalPath: '/cart',
      ogType: 'website',
      pageType: 'page',
      entityName: language === 'en' ? 'Cart' : 'Carrello',
      breadcrumbs: [
        { name: 'Home', path: '/' },
        { name: language === 'en' ? 'Cart' : 'Carrello', path: '/cart' },
      ],
    }
  }

  if (route.mode === 'page' && route.slug.trim().toLowerCase() === 'checkout') {
    return {
      title: `Checkout | ${siteName}`,
      description:
        language === 'en'
          ? 'Complete shipping details and payment confirmation for your order.'
          : 'Completa i dettagli di spedizione e la conferma pagamento per il tuo ordine.',
      canonicalPath: '/checkout',
      ogType: 'website',
      pageType: 'page',
      entityName: 'Checkout',
      breadcrumbs: [
        { name: 'Home', path: '/' },
        { name: language === 'en' ? 'Checkout' : 'Checkout', path: '/checkout' },
      ],
    }
  }

  const customerRouteMode = resolveCustomerRouteMode(route)
  if (!customerRouteMode) return null

  return {
    title:
      customerRouteMode === 'signup'
        ? `${language === 'en' ? 'Customer Sign Up' : 'Registrazione Cliente'} | ${siteName}`
        : customerRouteMode === 'login'
        ? `${language === 'en' ? 'Customer Login' : 'Login Cliente'} | ${siteName}`
        : customerRouteMode === 'activate'
        ? `${language === 'en' ? 'Activate Account' : 'Attiva Account'} | ${siteName}`
        : customerRouteMode === 'forgot-password'
        ? `${language === 'en' ? 'Recover Password' : 'Recupera Password'} | ${siteName}`
        : customerRouteMode === 'reset-password'
        ? `${language === 'en' ? 'Reset Password' : 'Reimposta Password'} | ${siteName}`
        : `${language === 'en' ? 'Customer Area' : 'Area Cliente'} | ${siteName}`,
    description:
      customerRouteMode === 'account'
        ? language === 'en'
          ? 'Manage profile, orders and support requests in your customer area.'
          : 'Gestisci profilo, ordini e richieste assistenza dalla tua area cliente.'
        : language === 'en'
        ? 'Sign in or sign up to use customer area.'
        : 'Accedi o registrati per usare l area cliente.',
    canonicalPath:
      customerRouteMode === 'signup'
        ? '/dashboard/signup'
      : customerRouteMode === 'login'
      ? '/dashboard/login'
      : customerRouteMode === 'activate'
      ? '/account/activate'
      : customerRouteMode === 'forgot-password'
      ? '/dashboard/forgot-password'
        : customerRouteMode === 'reset-password'
        ? '/dashboard/reset-password'
        : '/dashboard',
    ogType: 'website',
    pageType: 'page',
    entityName: language === 'en' ? 'Customer Area' : 'Area Cliente',
    breadcrumbs: [
      { name: 'Home', path: '/' },
      { name: language === 'en' ? 'Customer Area' : 'Area Cliente', path: '/dashboard' },
    ],
  }
}
